import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { examService } from '../services';
import { useAppSelector } from '../hooks';
import NotificationContainer from './Notification/NotificationContainer';
import { notificationActions } from '../redux/actions';

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at 20% 20%, #f1f8ff, #fff);
  display: flex;
  justify-content: center;
  padding: 40px 16px;
`;

const Card = styled.div`
  width: min(980px, 100%);
  background: white;
  border-radius: 14px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
  padding: 28px;
  border: 1px solid #eef2f7;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
`;

const BackButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #3b82f6;
  font-size: 18px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 8px;

  &:hover {
    background: #eff6ff;
    color: #1d4ed8;
  }

  @media (max-width: 640px) {
    padding: 6px 10px;
    font-size: 16px;
    margin-right: 4px;
  }
`;

const TitleSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  color: #0f172a;
  word-break: break-word;
`;

const Meta = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #475569;
  font-size: 14px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-weight: 600;
  font-size: 13px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const QuestionBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
  background: #f8fafc;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
`;

const QuestionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 10px;
  color: #0f172a;
`;

const SubText = styled.div`
  color: #475569;
  font-size: 14px;
  margin-bottom: 8px;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  background: white;
  &:hover { border-color: #3b82f6; }
`;

const Button = styled.button<{variant?: 'primary' | 'ghost'}>`
  padding: 12px 18px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;
  background: ${(p) => (p.variant === 'ghost' ? '#e2e8f0' : '#2563eb')};
  color: ${(p) => (p.variant === 'ghost' ? '#0f172a' : 'white')};
  width: 100%;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResultBox = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: #ecfdf3;
  border: 1px solid #bbf7d0;
  color: #166534;
  margin-top: 12px;
`;

const TimerBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #eef2ff;
  color: #3730a3;
  font-weight: 700;
`;

const StickyActions = styled.div`
  position: sticky;
  bottom: 0;
  padding-top: 12px;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #fff 24%);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.9fr 0.85fr;
  gap: 16px;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    
    > *:nth-child(1) { order: 1; } /* Questions */
    > *:nth-child(2) { order: 2; } /* Progress card */
    > *:nth-child(3) { order: 3; } /* Submit button */
  }
`;const RightCol = styled.div`
  position: sticky;
  top: 12px;

  @media (max-width: 768px) {
    position: static;
    order: 2;
  }
`;

const NavCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
`;

const NavHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-weight: 700;
  color: #0f172a;
`;

const TagGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 8px;
`;

const Tag = styled.button<{done?: boolean}>`
  border: 1px solid ${p => p.done ? '#22c55e' : '#cbd5e1'};
  background: ${p => p.done ? '#dcfce7' : '#fff'};
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 6px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
  text-align: center;
  &:hover { border-color: #2563eb; box-shadow: 0 4px 10px rgba(37, 99, 235, 0.15); }
`;

const WordBox = styled.div<{ selected?: boolean; disabled?: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  margin: 0;
  background: ${p => p.selected ? '#e0e7ff' : '#ffffff'};
  border: 2px solid ${p => p.selected ? '#6366f1' : '#cbd5e1'};
  border-radius: 7px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  font-size: 15px;
  color: ${p => p.selected ? '#4338ca' : '#0f172a'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${p => p.selected ? '0 4px 12px rgba(99, 102, 241, 0.25)' : '0 2px 6px rgba(0, 0, 0, 0.08)'};
  user-select: none;
  opacity: ${p => p.disabled ? 0.4 : 1};
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    ${p => !p.disabled && `
      border-color: #6366f1;
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
      transform: translateY(-2px) scale(1.05);
    `}
  }
  
  &:active {
    ${p => !p.disabled && `
      transform: scale(0.98);
    `}
  }
`;

const WordBoxesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  min-height: 60px;
  margin-bottom: 16px;
`;

const AnswerArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 12px;
  background: #fefce8;
  border: 2px dashed #fbbf24;
  border-radius: 12px;
  min-height: 60px;
  margin-bottom: 12px;
  position: relative;
  align-items: flex-start;
  
  &:empty:before {
    content: 'Ấn vào từ để sắp xếp, ấn lần nữa để đổi lại...';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #94a3b8;
    font-style: italic;
    pointer-events: none;
  }
`;

const SentencePreview = styled.div`
  padding: 12px 16px;
  background: #f0f9ff;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  font-size: 15px;
  color: #0f172a;
  font-weight: 500;
  margin-bottom: 12px;
  min-height: 44px;
  display: flex;
  align-items: center;
  
  &:empty:before {
    content: 'Câu của bạn sẽ hiển thị ở đây...';
    color: #94a3b8;
    font-style: italic;
  }
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PreviewText = styled.span`
  font-size: 15px;
  font-weight: 700;
  font-style: italic;
  color: #3b82f6;
  text-transform: none;
  letter-spacing: normal;
`;

const ExerciseHeader = styled.h3`
  color: #1d4ed8;
  font-size: 18px;
  font-weight: 800;
  margin: 18px 0 12px;
  border-bottom: 2px solid #bfdbfe;
  padding-bottom: 6px;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
`;

const ConfirmMessage = styled.div`
  font-size: 15px;
  color: #475569;
  margin-bottom: 16px;
  line-height: 1.6;
`;

const UndoneCList = styled.ul`
  margin: 12px 0 16px 0;
  padding-left: 20px;
  max-height: 200px;
  overflow-y: auto;
  background: #f8fafc;
  padding: 12px 20px;
  border-radius: 8px;
  border-left: 3px solid #ef4444;
`;

const UndoneItem = styled.li`
  color: #0f172a;
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ConfirmBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  padding: 12px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  background: ${(p) => (p.$variant === 'ghost' ? '#e2e8f0' : '#ef4444')};
  color: ${(p) => (p.$variant === 'ghost' ? '#0f172a' : 'white')};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const LoadingSpinner = styled.div`
  width: 80px;
  height: 80px;
  border: 8px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  margin: 20px auto;
`;

const ResultHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 3px solid #e2e8f0;
`;

const ResultTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const ScoreDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 8px;
  font-size: 48px;
  font-weight: 900;
  color: #3b82f6;
  margin: 16px 0;
  
  span:last-child {
    font-size: 32px;
    color: #64748b;
  }
`;

const AnswerDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

const AnswerDetailItem = styled.div<{ $isCorrect: boolean }>`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid ${(p) => p.$isCorrect ? '#10b981' : '#ef4444'};
`;

interface PublicExamPageProps {
  retryExam?: any;
  isRetryMode?: boolean;
  onRetrySubmit?: (answers: any) => Promise<void>;
}

const PublicExamPage: React.FC<PublicExamPageProps> = ({ 
  retryExam, 
  isRetryMode = false, 
  onRetrySubmit 
}) => {
  const { shareLink } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useAppSelector((s: any) => s.authentication?.userInfo);

  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [participantName, setParticipantName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [undoneQuestions, setUndoneQuestions] = useState<any[]>([]);
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Normalize answer string for comparison (same logic as backend)
  const normalizeAnswer = (value: string): string => {
    if (typeof value !== 'string') return value;
    
    let normalized = value.trim();
    // Normalize smart quotes to ASCII
    normalized = normalized.replace(/[‘’]/g, "'");
    normalized = normalized.replace(/[“”]/g, '"');
    // Remove trailing period(s) and spaces: "abc ." → "abc"
    normalized = normalized.replace(/\s*\.+\s*$/g, '');
    // Normalize spaces around punctuation: "a,b" | "a , b" → "a,b"
    normalized = normalized.replace(/\s*,\s*/g, ',');
    normalized = normalized.replace(/\s*\?\s*/g, '?');
    // Collapse multiple spaces to single space
    normalized = normalized.replace(/\s+/g, ' ');
    return normalized.toLowerCase();
  };

  useEffect(() => {
    console.log('authUser:', authUser); // Debug log
    if (authUser?.name) {
      console.log('Setting name from authUser.name:', authUser.name);
      setParticipantName(authUser.name);
    } else if (authUser?.userName) {
      console.log('Setting name from authUser.userName:', authUser.userName);
      setParticipantName(authUser.userName);
    } else if (authUser?.email) {
      console.log('Setting name from authUser.email:', authUser.email);
      setParticipantName(authUser.email);
    }
  }, [authUser]);

  useEffect(() => {
    const load = async () => {
      // If in retry mode, use provided exam
      if (isRetryMode && retryExam) {
        setExam(retryExam);
        setLoading(false);
        return;
      }

      // Otherwise load from API using shareLink
      if (!shareLink) return;
      setLoading(true);
      setError(null);
      try {
        const res = await examService.getByShareLink(shareLink);
        setExam(res.data);
      } catch (e: any) {
        setError(e?.message || 'Không tải được đề thi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareLink, isRetryMode, retryExam]);

  // Timer (count up) and remaining if duration exists
  useEffect(() => {
    if (!exam) return;
    setElapsedSec(0);
    setStartTime(new Date()); // Record when exam starts
    const timer = setInterval(() => {
      setElapsedSec((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [exam]);

  const remainingSec = useMemo(() => {
    if (!exam?.duration) return null;
    const total = exam.duration * 60;
    return Math.max(0, total - elapsedSec);
  }, [exam, elapsedSec]);

  const fmt = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const groupedByType = useMemo(() => {
    if (!exam?.exam_questions) return [];
    const grouped: Record<number, { typeId: number; typeInfo: any; questions: any[] }> = {};
    exam.exam_questions.forEach((eq: any) => {
      const typeId = eq.question?.question_type_id || 0;
      if (!grouped[typeId]) {
        grouped[typeId] = { typeId, typeInfo: eq.question?.questionType, questions: [] };
      }
      grouped[typeId].questions.push(eq);
    });
    return Object.values(grouped).sort((a, b) => a.typeId - b.typeId);
  }, [exam]);

  const flatQuestions = useMemo(() => {
    const list: any[] = [];
    groupedByType.forEach((g) => {
      g.questions.forEach((eq) => list.push(eq));
    });
    return list;
  }, [groupedByType]);

  // Lookup questions by id to reuse full content when result payload lacks fields
  const questionLookup = useMemo(() => {
    const map = new Map<number, any>();
    flatQuestions.forEach((eq) => {
      if (eq?.question?.id) {
        map.set(eq.question.id, eq.question);
      }
    });
    return map;
  }, [flatQuestions]);

  const questionNumberMap = useMemo(() => {
    const m: Record<number, number> = {};
    let counter = 1;
    groupedByType.forEach((g) => {
      g.questions.forEach((eq) => {
        if (eq.question?.id) {
          m[eq.question.id] = counter;
          counter += 1;
        }
      });
    });
    return m;
  }, [groupedByType]);

  const questionTags = useMemo(() => {
    const tags: { id: number; label: number; typeId: number }[] = [];
    groupedByType.forEach((g) => {
      g.questions.forEach((eq) => {
        const id = eq.question?.id;
        if (id) {
          tags.push({ id, label: tags.length + 1, typeId: eq.question?.question_type_id });
        }
      });
    });
    return tags;
  }, [groupedByType]);

  // Normalize True/False values to single-letter tokens for consistent comparison
  const normalizeTF = (val: any) => {
    const v = String(val ?? '').trim().toLowerCase();
    if (v === 't' || v === 'true') return 'T';
    if (v === 'f' || v === 'false') return 'F';
    return v ? v[0].toUpperCase() : '';
  };

  const formatTFLabel = (norm: string) => {
    if (norm === 'T') return 'True';
    if (norm === 'F') return 'False';
    return norm || '—';
  };

  const parseTFAnswerParts = (answer: any, expectedLength?: number) => {
    const parts = Array.isArray(answer)
      ? answer
      : String(answer ?? '')
          .split(' | ')
          .map((p: string) => p.trim())
          .filter((p) => p.length > 0);
    const normalized = parts.map((p) => normalizeTF(p));
    if (expectedLength && normalized.length < expectedLength) {
      return [...normalized, ...Array(expectedLength - normalized.length).fill('')];
    }
    return normalized;
  };

  const renderQuestionText = (q: any) => {
    let c = q.content || {};
    if (typeof c === 'string') {
      try {
        c = JSON.parse(c);
      } catch {
        c = {};
      }
    }
    const typeId = q.question_type_id;
    
      // Type 1, 2, 5, 8, 10, 12, 13: plain text
    if (c.text) return c.text;
    if (c.original_sentence) return c.original_sentence;
      if (c.sentence) return c.sentence;
    
    // Type 3: Sentence Ordering - show words
    if (typeId === 3 && c.words) return c.words.join(' / ');
    
    // Type 4: Write Sentence - show first prompt
    if (typeId === 4 && c.prompts && c.prompts.length > 0) return c.prompts[0].text;
    
    // Type 6, 7, 9: Reading/Fill Blanks - show passage
    if ((typeId === 6 || typeId === 7 || typeId === 9) && c.passage) return c.passage;

    // Type 12: True/False - show passage and statements
    if (typeId === 12) {
      const passageBlock = c.passage ? `${c.passage}\n` : '';
      if (Array.isArray(c.statements) && c.statements.length > 0) {
        const stmtText = c.statements.map((s: any, idx: number) => `${idx + 1}) ${s?.text || ''}`).join(' | ');
        return `${passageBlock}${stmtText}`.trim();
      }
      return c.passage || c.text || '';
    }
    
    return '';
  };

  const isAnswered = (qid: number) => {
    const v = answers[qid];
    if (v === undefined || v === null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) {
      // Find the question to determine its type
      const eq = flatQuestions.find((eq: any) => eq.question?.id === qid);
      const typeId = eq?.question?.question_type_id;
      
      // Type 3: Sentence Ordering - array of word indices (numbers)
      if (typeId === 3) {
        return v.length > 0; // Just check if at least one word is selected
      }
      
      // Type 4: Write Sentence - array of strings (prompts)
      if (typeId === 4) {
        return v.length > 0 && v.every((ans) => typeof ans === 'string' && ans.trim().length > 0);
      }
      
      // Other array types - just check length
      return v.length > 0;
    }
    return true;
  };

  const scrollToQuestion = (qid: number) => {
    const el = questionRefs.current[qid];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderQuestionBody = (q: any) => {
    let c = q.content || {};
    if (typeof c === 'string') {
      try {
        c = JSON.parse(c);
      } catch {
        c = {};
      }
    }
    const typeId = q.question_type_id;

    // Type 1: Multiple choice
    if (typeId === 1 && c.options) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {c.options.map((opt: string, i: number) => (
            <OptionRow key={i}>
              <input
                type="radio"
                name={`q_${q.id}`}
                checked={answers[q.id] === opt}
                onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
              />
              <span><strong>{String.fromCharCode(65 + i)}.</strong> {opt}</span>
            </OptionRow>
          ))}
        </div>
      );
    }

    // Type 3: Sentence Ordering - interactive word boxes
    if (typeId === 3 && c.words) {
      const selectedWords = answers[q.id] || [];
      const isWordSelected = (index: number) => selectedWords.includes(index);
      
      const handleWordClick = (index: number) => {
        setAnswers((prev) => {
          const current = prev[q.id] || [];
          if (current.includes(index)) {
            // Remove word from answer
            return { ...prev, [q.id]: current.filter((i: number) => i !== index) };
          } else {
            // Add word to answer
            return { ...prev, [q.id]: [...current, index] };
          }
        });
      };
      
      const sentenceText = selectedWords.map((idx: number) => c.words[idx]).join(' ');
      
      return (
        <div>
          <SectionLabel>Các từ cần sắp xếp:</SectionLabel>
          <WordBoxesContainer>
            {c.words.map((word: string, idx: number) => (
              <WordBox
                key={idx}
                selected={isWordSelected(idx)}
                disabled={isWordSelected(idx)}
                onClick={() => !isWordSelected(idx) && handleWordClick(idx)}
              >
                {word}
              </WordBox>
            ))}
          </WordBoxesContainer>
          
          <SectionLabel>
            Câu trả lời của bạn:
            {sentenceText && <PreviewText>{sentenceText}</PreviewText>}
          </SectionLabel>
          <AnswerArea>
            {selectedWords.map((idx: number, position: number) => (
              <WordBox
                key={`selected-${idx}-${position}`}
                selected={true}
                onClick={() => handleWordClick(idx)}
              >
                {c.words[idx]}
              </WordBox>
            ))}
          </AnswerArea>
        </div>
      );
    }

    // Type 4: Write Sentence - prompts array
    if (typeId === 4 && c.prompts) {
      const promptAnswers = Array.isArray(answers[q.id]) ? answers[q.id] : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {c.prompts.map((p: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <TextArea
                placeholder={`Trả lời ...`}
                value={promptAnswers[idx] || ''}
                onChange={(e) => {
                  const newAnswers = [...promptAnswers];
                  newAnswers[idx] = e.target.value;
                  setAnswers((prev) => ({ ...prev, [q.id]: newAnswers }));
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    // Type 5: rewrite sentence
    if (typeId === 5 && c.original_sentence) {
      return (
        <>
          {c.instruction && <SubText style={{ fontStyle: 'italic' }}>→ {c.instruction + ' ....'}</SubText>}
          <TextArea
            value={answers[q.id] ?? `${c.instruction} `}
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          />
        </>
      );
    }

    // Type 6: Reading Essay - passage + questions
    if (typeId === 6 && c.passage) {
      return (
        <>
          {c.questions && c.questions.map((q: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Câu {idx + 1}: {q.question_text}
              </div>
              <TextArea
                placeholder={`Trả lời câu hỏi ${idx + 1}...`}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                style={{ minHeight: '80px' }}
              />
            </div>
          ))}
        </>
      );
    }

    const formatPassageWithIndexes = (passage: string, countHint: number) => {
      const blankRegex = /_{3,}|\.{3,}/g;
      let idx = 1;
      const blanksInText = (passage.match(blankRegex) || []).length;
      const total = Math.max(blanksInText, countHint);
      return passage.replace(blankRegex, () => {
        if (idx > total) idx = total; // avoid overshooting when countHint > blanksInText
        const text = `(${idx}) ____`;
        idx += 1;
        return text;
      });
    };

    // Type 7: Reading MCQ - passage + MCQ questions
    if (typeId === 7 && c.passage) {
      return (
        <>
          <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px' }}>
            {c.passage}
          </SubText>
          {c.questions && c.questions.map((q: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Câu {idx + 1}: {q.question_text}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {q.options && q.options.map((ans: string, ansIdx: number) => {
                  const optionLabel = String.fromCharCode(65 + ansIdx);
                  return (
                    <OptionRow key={ansIdx}>
                      <input
                        type="radio"
                        name={`q_${q.id}`}
                        checked={answers[q.id] === ans}
                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: ans }))}
                      />
                      <span><strong>{optionLabel}.</strong> {ans}</span>
                    </OptionRow>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      );
    }

    // Type 9: Fill Blanks MCQ (passage + options cho từng chỗ trống)
    if (typeId === 9 && c.passage && Array.isArray(c.questions)) {
      const blankRegex = /_{3,}|\.{3,}/g;
      const blanksInText = (c.passage.match(blankRegex) || []).length;
      const blanksCount = Math.max(blanksInText, c.questions.length);
      const indexedPassage = formatPassageWithIndexes(c.passage, blanksCount);
      const currentAnswers = Array.isArray(answers[q.id]) ? answers[q.id] : Array(blanksCount).fill('');

      return (
        <>
          {c.questions.map((subQ: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Chỗ trống {idx + 1}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(subQ.options || []).map((opt: string, optIdx: number) => {
                  const optionLabel = String.fromCharCode(65 + optIdx);
                  return (
                    <OptionRow key={optIdx}>
                      <input
                        type="radio"
                        name={`q_${q.id}_blank_${idx}`}
                        checked={currentAnswers[idx] === opt}
                        onChange={() => {
                          const newAnswers = [...currentAnswers];
                          newAnswers[idx] = opt;
                          setAnswers((prev) => ({ ...prev, [q.id]: newAnswers }));
                        }}
                      />
                      <span><strong>{optionLabel}.</strong> {opt}</span>
                    </OptionRow>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      );
    }

    // Type 8: Fill Blanks Word Bank
    if (typeId === 8 && c.text && c.word_bank) {
      // Parse text to find blanks (marked by ___ or multiple dots)
      const blankRegex = /_{3,}|\.{3,}/g;
      const parts = c.text.split(blankRegex);
      const blanksCount = (c.text.match(blankRegex) || []).length;
      
      // Initialize answers as array if not exists
      const currentAnswers = Array.isArray(answers[q.id]) ? answers[q.id] : Array(blanksCount).fill('');
      
      return (
        <>
          {/* Word Bank */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            marginBottom: '16px',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px dashed #dee2e6'
          }}>
            <strong style={{ width: '100%', marginBottom: '4px', color: '#495057' }}>Word Bank:</strong>
            {c.word_bank.map((word: string, idx: number) => (
              <span
                key={idx}
                style={{
                  padding: '6px 12px',
                  background: 'white',
                  border: '2px solid #007bff',
                  borderRadius: '6px',
                  fontWeight: 600,
                  color: '#007bff',
                  fontSize: '14px'
                }}
              >
                {word}
              </span>
            ))}
          </div>
          
          {/* Text with dropdowns */}
          <div style={{ lineHeight: '2', fontSize: '15px' }}>
            {parts.map((part: string, idx: number) => (
              <span key={idx}>
                {part}
                {idx < blanksCount && (
                  <select
                    value={currentAnswers[idx] || ''}
                    onChange={(e) => {
                      const newAnswers = [...currentAnswers];
                      newAnswers[idx] = e.target.value;
                      setAnswers((prev) => ({ ...prev, [q.id]: newAnswers }));
                    }}
                    style={{
                      padding: '4px 8px',
                      margin: '0 4px',
                      border: '2px solid #007bff',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer',
                      fontWeight: 600,
                      minWidth: '100px'
                    }}
                  >
                    <option value="">---</option>
                    {c.word_bank.map((word: string, wIdx: number) => (
                      <option key={wIdx} value={word}>{word}</option>
                    ))}
                  </select>
                )}
              </span>
            ))}
          </div>
        </>
      );
    }

    // Type 10: Error Identification - only options (sentence shown in title)
    if (typeId === 10 && c.sentence && c.options) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {c.options.map((opt: string, i: number) => (
            <OptionRow key={i}>
              <input
                type="radio"
                name={`q_${q.id}`}
                checked={answers[q.id] === String(i)}
                onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: String(i) }))}
              />
              <span><strong>{String.fromCharCode(65 + i)}.</strong> {opt}</span>
            </OptionRow>
          ))}
        </div>
      );
    }

    // Type 11: Make Question - show sentence with underline, text input for question
    if (typeId === 11 && c.sentence) {
      const sentence = c.sentence;
      const underlined = c.underlined || '';
      return (
        <>
          <SubText style={{ fontWeight: 500, marginBottom: '12px', lineHeight: 1.6 }}>
            {underlined && sentence.includes(underlined) ? (
              <>
                {sentence.split(underlined)[0]}
                <u style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{underlined}</u>
                {sentence.split(underlined)[1] || ''}
              </>
            ) : (
              sentence
            )}
          </SubText>
          <TextArea
            placeholder="Đặt câu hỏi cho phần gạch chân..."
            value={answers[q.id] || ''}
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          />
        </>
      );
    }

    // Type 12: True/False - passage + multiple statements
    if (typeId === 12 && Array.isArray(c.statements)) {
      const passage = c.passage || '';
      const currentAnswers = Array.isArray(answers[q.id])
        ? (answers[q.id] as string[]).map((val) => normalizeTF(val))
        : Array(c.statements.length).fill('');
      return (
        <>
          {passage && (
            <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px' }}>
              {passage} {q.id && <span style={{ fontStyle: 'italic', fontSize: 12, color: '#666' }}>[{q.id}]</span>}
            </SubText>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {c.statements.map((stmt: any, idx: number) => (
              <div key={idx} style={{ padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff' }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>
                  Câu {idx + 1}: {stmt.text}
                  {!passage && q.id && idx === 0 && (
                    <span style={{ fontStyle: 'italic', fontSize: 12, color: '#666' }}> [{q.id}]</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {[
                    { value: 'T', label: '✓ Đúng (True)' },
                    { value: 'F', label: '✗ Sai (False)' },
                  ].map(({ value, label }) => (
                    <OptionRow key={value} style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type="radio"
                        name={`q_${q.id}_stmt_${idx}`}
                        checked={currentAnswers[idx] === value}
                        onChange={() => {
                          const next = [...currentAnswers];
                          next[idx] = value;
                          setAnswers((prev) => ({ ...prev, [q.id]: next }));
                        }}
                      />
                      <span>{label}</span>
                    </OptionRow>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    // Type 2, 12, 13 and fallback: show textarea
    return (
      <TextArea
        placeholder="Nhập câu trả lời..."
        value={answers[q.id] || ''}
        onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
      />
    );
  };

  const handleSubmit = async () => {
    if (!exam) return;
    if (!participantName.trim()) {
      setError('Vui lòng nhập tên trước khi nộp bài');
      return;
    }

    // Check for unanswered questions
    const unanswered: any[] = [];
    flatQuestions.forEach((eq: any, idx: number) => {
      const qid = eq.question?.id;
      if (!isAnswered(qid)) {
        unanswered.push({
          number: questionNumberMap[qid] || idx + 1,
          text: renderQuestionText(eq.question),
        });
      }
    });

    // If there are unanswered questions, show confirmation modal
    if (unanswered.length > 0) {
      setUndoneQuestions(unanswered);
      setShowConfirmModal(true);
      return;
    }

    // All questions answered, proceed to submit
    await performSubmit();
  };

  const performSubmit = async () => {
    if (!exam) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setShowConfirmModal(false);
    try {
      // Build question type map for processing Type 4 answers
      const questionTypeMap = new Map<number, number>();
      flatQuestions.forEach((eq: any) => {
        if (eq.question?.id && eq.question?.question_type_id) {
          questionTypeMap.set(Number(eq.question.id), Number(eq.question.question_type_id));
        }
      });

      const payload = {
        participant_name: participantName.trim(),
        started_at: startTime?.toISOString(),
        elapsed_seconds: elapsedSec,
        answers: Object.entries(answers).map(([question_id, answer]) => {
          const qid = Number(question_id);
          const typeId = questionTypeMap.get(qid);
          
          // Convert array answers to strings for specific types
          let finalAnswer = answer;
          if (Array.isArray(answer)) {
            if (typeId === 3) {
              // Type 3 (Sentence Ordering): answer is array of word indices
              // Find the question to get the words array
              const question = flatQuestions.find((eq: any) => eq.question?.id === qid);
              if (question?.question?.content?.words) {
                const words = question.question.content.words;
                finalAnswer = answer.map((idx: number) => words[idx]).join(' ');
              } else {
                finalAnswer = '';
              }
            } else if (typeId === 4) {
              // Type 4 (Write Sentence with prompts): join with ' | '
              finalAnswer = answer.join(' | ');
            } else if (typeId === 8) {
              // Type 8 (Fill Blanks Word Bank): join with ', '
              finalAnswer = answer.join(', ');
            } else if (typeId === 9) {
              // Type 9 (Fill Blanks MCQ): join with ' | '
              finalAnswer = answer.join(' | ');
            } else if (typeId === 12) {
              // Type 12 (True/False multiple statements)
              finalAnswer = answer
                .map((v) => {
                  const norm = normalizeTF(v);
                  if (norm === 'T' || norm === 'F') return norm;
                  return String(v ?? '').trim();
                })
                .join(' | ');
            }
          }
          
          return { question_id: qid, answer: finalAnswer };
        }),
      };
      console.log('📤 Submitting answers:', payload);

      // Handle retry mode differently
      if (isRetryMode && onRetrySubmit) {
        await onRetrySubmit(payload.answers);
        return;
      }

      const res = await examService.submitByShareLink(shareLink as string, payload);
      dispatch(notificationActions.addNotification('Nộp bài thành công!', 'SUCCESS'));
      
      // For guests: show result details
      if (!authUser) {
        setResult(res.data); // Store submission result
        setAnswers({});
        setParticipantName('');
        setElapsedSec(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // For authenticated users: show loading then navigate
        setAnswers({});
        setElapsedSec(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          navigate('/student');
        }, 1500);
      }
    } catch (e: any) {
      setError(e?.message || 'Nộp bài thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    setUndoneQuestions([]);
  };

  const submitDisabled = !participantName.trim() || submitting || !flatQuestions.length;

  return (
    <>
      <NotificationContainer />
      
      {/* Loading overlay for authenticated users after submission */}
      {submitting && authUser && (
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Đang xử lý bài làm của bạn...</LoadingText>
        </LoadingOverlay>
      )}
      
      {/* Result display for guest users */}
      {result && !authUser && result.submission_details && (
        <Page>
          <ResultCard>
            <ResultHeader>
              <ResultTitle>🎉 Kết quả bài thi</ResultTitle>
              <div style={{ fontSize: '18px', color: '#64748b', marginBottom: '8px' }}>
                {exam?.title}
              </div>
              <ScoreDisplay>
                <span>{result.total_score}</span>
                <span>/ {result.max_score}</span>
              </ScoreDisplay>
              <div style={{ fontSize: '16px', color: '#64748b' }}>
                {result.submission_details.participant_name}
              </div>
            </ResultHeader>
            
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f1f5f9', borderRadius: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
                <div>
                  <strong>Thời gian làm bài:</strong>{' '}
                  {(() => {
                    const started = new Date(result.submission_details.started_at);
                    const finished = new Date(result.submission_details.finished_at);
                    const diffMs = finished.getTime() - started.getTime();
                    const minutes = Math.floor(diffMs / 60000);
                    const seconds = Math.floor((diffMs % 60000) / 1000);
                    return `${minutes} phút ${seconds} giây`;
                  })()}
                </div>
                <div>
                  <strong>Nộp lúc:</strong>{' '}
                  {new Date(result.submission_details.finished_at).toLocaleString('vi-VN')}
                </div>
              </div>
            </div>

            <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px', color: '#1e293b' }}>
              Chi tiết các câu trả lời
            </h3>
            <AnswerDetailsList>
              {(() => {
                console.log('🎬 RESULT SECTION - exam exists?', !!exam);
                console.log('🎬 RESULT SECTION - exam?.exam_questions?.length:', exam?.exam_questions?.length);
                console.log('🎬 RESULT SECTION - groupedByType.length:', groupedByType?.length);
                console.log('🎬 RESULT SECTION - flatQuestions.length:', flatQuestions?.length);
                
                if (!exam || !exam.exam_questions || groupedByType.length === 0) {
                  console.warn('⚠️ MISSING DATA: Cannot rebuild grouped structure. Exam:', !!exam, 'Questions:', exam?.exam_questions?.length, 'GroupedByType:', groupedByType?.length);
                }
                
                // Group user_answers by question type, preserving exam order
                const userAnswersMap = new Map();
                (result.submission_details.user_answers || []).forEach((ua: any) => {
                  const qid = Number(ua.question_id || ua.question?.id);
                  userAnswersMap.set(qid, ua);
                });

                console.log('🔍 DEBUG - groupedByType:', groupedByType);
                console.log('🔍 DEBUG - userAnswersMap size:', userAnswersMap.size);
                console.log('🔍 DEBUG - questionNumberMap:', questionNumberMap);

                // Build groups matching the original exam structure
                const groups: Array<{ typeId: number; typeInfo: any; answers: any[] }> = [];
                groupedByType.forEach((group, gIdx) => {
                  const answersInGroup: any[] = [];
                  group.questions.forEach((eq: any) => {
                    const qid = eq.question?.id;
                    const ua = userAnswersMap.get(qid);
                    if (ua) {
                      answersInGroup.push(ua);
                    }
                  });
                  if (answersInGroup.length > 0) {
                    groups.push({
                      typeId: group.typeId,
                      typeInfo: group.typeInfo,
                      answers: answersInGroup
                    });
                  }
                  console.log(`🔍 DEBUG - Group ${gIdx} (typeId: ${group.typeId}):`, answersInGroup.length, 'answers');
                });

                console.log('🔍 DEBUG - Final groups:', groups);
                return groups;
              })().map((group, exerciseIdx) => {
                const typeInfo = group.typeInfo || {};
                const engTitle = typeInfo.eng_title || '';
                const vnTitle = typeInfo.vn_title || '';
                const headerText = `Exercise ${exerciseIdx + 1}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;

                console.log(`🎯 Rendering Exercise ${exerciseIdx + 1}:`, headerText, 'with', group.answers.length, 'answers');
                
                return (
                  <div key={`exercise-${exerciseIdx}`} style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      color: '#1d4ed8',
                      fontSize: '16px',
                      fontWeight: 700,
                      margin: '0 0 12px 0',
                      borderBottom: '2px solid #bfdbfe',
                      paddingBottom: '6px'
                    }}>
                      {headerText}
                    </h4>
                    {group.answers.map((ua: any, idx: number) => {
                      // Prefer payload question, fallback to lookup by id from exam data (handle string/number ids)
                      const lookupKey = ua.question_id ? Number(ua.question_id) : (ua.question?.id ? Number(ua.question.id) : undefined);
                      const qFallback = lookupKey !== undefined ? questionLookup.get(lookupKey) : undefined;
                      const q = ua.question || qFallback || {};
                      const globalQuestionNumber = lookupKey !== undefined ? (questionNumberMap[lookupKey] || idx + 1) : idx + 1;
                      
                      console.log(`  📝 Question ${globalQuestionNumber} (id: ${lookupKey}):`, q?.content?.text?.substring(0, 50));

                      // Parse content same way as ExamsManage
                      let content: any = q.content || {};
                      if (typeof content === 'string') {
                        try {
                          content = JSON.parse(content);
                        } catch {
                          content = {};
                        }
                      }
                      // Fallback typeId from lookup if missing
                      const typeId = q.question_type_id || qFallback?.question_type_id;
                
                // Render question text - can be JSX or string
                const renderQuestionText = () => {
                  // Type 3: Sentence Ordering - show words/items or correct_sentence as fallback
                  if (typeId === 3) {
                    const words = content.words || content.items;
                    if (Array.isArray(words) && words.length > 0) return words.join(' / ');
                    if (content.correct_sentence) return content.correct_sentence;
                  }
                  // Type 4: Write Sentence - show first prompt or text/correct sentence
                  if (typeId === 4) {
                    if (content.prompts && content.prompts.length > 0) return content.prompts[0].text;
                    if (content.text) return content.text;
                    if (content.correct_sentence) return content.correct_sentence;
                  }
                  // Type 6, 7, 9: Reading - show passage with indexed blanks
                  if ((typeId === 6 || typeId === 7 || typeId === 9) && content.passage) {
                    const blankRegex = /_{3,}|\.{3,}/g;
                    let blankIndex = 1;
                    return content.passage.replace(blankRegex, () => `(${blankIndex++}) ____`);
                  }
                  // Type 8: Fill Blanks Word Bank - show text
                  if (typeId === 8 && content.text) return content.text;
                  // Type 10: Error Identification - show sentence
                  if (typeId === 10 && content.sentence) return content.sentence;
                  // Type 11: Make Question - show sentence with underline
                  if (typeId === 11) {
                    const trySentenceBlock = (obj: any) => {
                      if (!obj || !obj.sentence) return null;
                      const sentence = obj.sentence;
                      const underlined = obj.underlined || '';
                      if (underlined && sentence.includes(underlined)) {
                        const parts = sentence.split(underlined);
                        return (
                          <>
                            {parts[0]}<u style={{fontWeight:'bold'}}>{underlined}</u>{parts[1]||''}
                          </>
                        );
                      }
                      return sentence;
                    };
                    const fromContent = trySentenceBlock(content);
                    if (fromContent) return fromContent;
                    const fallbackQ = lookupKey !== undefined ? questionLookup.get(lookupKey) : undefined;
                    if (fallbackQ) {
                      let fbContent: any = fallbackQ.content || {};
                      if (typeof fbContent === 'string') {
                        try { fbContent = JSON.parse(fbContent); } catch { fbContent = {}; }
                      }
                      const fromFb = trySentenceBlock(fbContent);
                      if (fromFb) return fromFb;
                    }
                  }
                  // Type 12: True/False - show passage and statements
                  if (typeId === 12) {
                    const passageBlock = content.passage ? <div style={{ marginBottom: 6 }}>{content.passage}</div> : null;
                    if (Array.isArray(content.statements) && content.statements.length > 0) {
                      return (
                        <>
                          {passageBlock}
                          <div>{content.statements.map((s: any, idx: number) => `${idx + 1}) ${s?.text || ''}`).join(' | ')}</div>
                        </>
                      );
                    }
                    return content.passage || content.text || '(Không có nội dung)';
                  }
                  // Default: try text, original_sentence, sentence in order
                  const tryContent = (cObj: any) => cObj?.text || cObj?.original_sentence || cObj?.sentence;
                  // If content is missing, try fallback question from lookup
                  if (!content || Object.keys(content).length === 0) {
                    const fallbackQ = lookupKey !== undefined ? questionLookup.get(lookupKey) : undefined;
                    if (fallbackQ) {
                      let fbContent: any = fallbackQ.content || {};
                      if (typeof fbContent === 'string') {
                        try { fbContent = JSON.parse(fbContent); } catch { fbContent = {}; }
                      }
                      const fbText = tryContent(fbContent);
                      if (fbText) return fbText;
                    }
                  }
                  return tryContent(content) || '(Không có nội dung)';
                };

                const formattedAnswer = (() => {
                  if (typeId === 10 && Array.isArray(content.options)) {
                    const userIdx = Number(ua.answer);
                    if (!Number.isNaN(userIdx) && content.options[userIdx] !== undefined) {
                      return `${String.fromCharCode(65 + userIdx)}. ${content.options[userIdx]}`;
                    }
                  }
                  if (typeId === 12 && Array.isArray(content.statements)) {
                    const parts = parseTFAnswerParts(ua.answer, content.statements.length);
                    return content.statements.map((_: any, idx: number) => {
                      const val = formatTFLabel(parts[idx] || '');
                      return `${idx + 1}) ${val}`;
                    }).join(' | ');
                  }
                  return ua.answer || '(Trống)';
                })();

                const formattedCorrect = (() => {
                  // Type 3: Sentence Ordering - correct_answers array or correct_sentence (backward compatibility)
                  if (typeId === 3) {
                    if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                      return content.correct_answers.join(' | ');
                    }
                    if (content?.correct_sentence) {
                      return content.correct_sentence;
                    }
                  }
                  // Type 4: Write Sentence - prompts array with multiple answers per prompt
                  if (typeId === 4 && content?.prompts && Array.isArray(content.prompts)) {
                    const allCorrectAnswers: string[] = [];
                    content.prompts.forEach((p: any) => {
                      if (p.answers && Array.isArray(p.answers)) {
                        allCorrectAnswers.push(...p.answers.filter(Boolean));
                      } else if (p.answer) {
                        allCorrectAnswers.push(p.answer);
                      }
                    });
                    if (allCorrectAnswers.length > 0) {
                      return allCorrectAnswers.join(' | ');
                    }
                  }
                  // Type 5: Rewrite Sentence - correct_answers array or correct_answer (backward compatibility)
                  if (typeId === 5) {
                    if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                      return content.correct_answers.join(' | ');
                    }
                    if (content?.correct_answer) {
                      return content.correct_answer;
                    }
                  }
                  if (typeId === 10 && Array.isArray(content.options)) {
                    // Show: B. enjoy → enjoys (correct answer format)
                    const wrongIdx = content.wrong_index;
                    const correction = content.correct_answer || content.correction || '';
                    if (wrongIdx !== undefined && content.options[wrongIdx] !== undefined) {
                      const label = String.fromCharCode(65 + wrongIdx);
                      return `${label}. ${content.options[wrongIdx]} → ${correction}`;
                    }
                  }
                  if (typeId === 11) {
                    // Support multiple correct questions
                    if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                      return content.correct_questions.join(' | ');
                    }
                    if (content.correct_question) return content.correct_question;
                    const fbQ = lookupKey !== undefined ? questionLookup.get(lookupKey) : undefined;
                    if (fbQ) {
                      let fbContent: any = fbQ.content || {};
                      if (typeof fbContent === 'string') {
                        try { fbContent = JSON.parse(fbContent); } catch { fbContent = {}; }
                      }
                      if (fbContent.correct_questions && Array.isArray(fbContent.correct_questions) && fbContent.correct_questions.length > 0) {
                        return fbContent.correct_questions.join(' | ');
                      }
                      if (fbContent.correct_question) return fbContent.correct_question;
                      if (fbQ?.correctAnswers?.[0]?.answer) return fbQ.correctAnswers[0].answer;
                    }
                    if (q?.correctAnswers?.[0]?.answer) return q.correctAnswers[0].answer;
                    return '—';
                  }
                  if (typeId === 12 && Array.isArray(content.statements)) {
                    return content.statements
                      .map((s: any, idx: number) => `${idx + 1}) ${formatTFLabel(s.is_true ? 'T' : 'F')}`)
                      .join(' | ');
                  }
                  return q?.correctAnswers?.[0]?.answer || (() => {
                    const fbQ = lookupKey !== undefined ? questionLookup.get(lookupKey) : undefined;
                    return fbQ?.correctAnswers?.[0]?.answer || '—';
                  })();
                })();

                const derivedTF = (() => {
                  if (typeId !== 12 || !Array.isArray(content.statements)) return null;
                  const expected = content.statements.map((s: any) => (s.is_true ? 'T' : 'F'));
                  const userParts = parseTFAnswerParts(ua.answer, expected.length);
                  const correctCount = expected.reduce((acc: number, exp: string, i: number) => acc + (userParts[i] === exp ? 1 : 0), 0);
                  const totalSub = expected.length;
                  return {
                    expected,
                    userParts,
                    correctCount,
                    totalSub,
                    isAllCorrect: totalSub > 0 && correctCount === totalSub,
                  };
                })();

                // Override is_correct for essay-type questions (3,4,5,6,11) using normalized comparison
                const overrideEssayCorrectness = (() => {
                  // Only for essay types: 3 (SentenceOrdering), 4 (WriteSentence), 5 (RewriteSentence), 6 (ReadingEssay), 11 (MakeQuestion)
                  if (![3, 4, 5, 6, 11].includes(typeId)) return null;
                  
                  const studentAnswer = ua.answer;
                  if (!studentAnswer) return false;

                  // Type 3: compare with correct_answers array or correct_sentence
                  if (typeId === 3) {
                    const correctAnswers: string[] = [];
                    if (content?.correct_answers && Array.isArray(content.correct_answers)) {
                      correctAnswers.push(...content.correct_answers);
                    } else if (content?.correct_sentence) {
                      correctAnswers.push(content.correct_sentence);
                    }
                    // Check if student answer matches ANY correct answer
                    return correctAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                  }

                  // Type 4: compare with multiple answers per prompt
                  if (typeId === 4 && content?.prompts && Array.isArray(content.prompts)) {
                    const allCorrectAnswers: string[] = [];
                    content.prompts.forEach((p: any) => {
                      if (p.answers && Array.isArray(p.answers)) {
                        allCorrectAnswers.push(...p.answers.filter(Boolean));
                      } else if (p.answer) {
                        allCorrectAnswers.push(p.answer);
                      }
                    });
                    // Check if student answer matches ANY correct answer
                    return allCorrectAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                  }

                  // Type 5: compare with correct_answers array or correct_answer
                  if (typeId === 5) {
                    const correctAnswers: string[] = [];
                    if (content?.correct_answers && Array.isArray(content.correct_answers)) {
                      correctAnswers.push(...content.correct_answers);
                    } else if (content?.correct_answer) {
                      correctAnswers.push(content.correct_answer);
                    }
                    // Check if student answer matches ANY correct answer
                    return correctAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                  }

                  // Type 6: compare with correct_answers (if single question) or check sub-questions
                  if (typeId === 6 && content?.questions && Array.isArray(content.questions)) {
                    // For reading essay with sub-questions, backend already handles this
                    // Just trust backend result
                    return null;
                  }

                  // Type 11: compare with correct_questions array or correct_question
                  if (typeId === 11) {
                    const correctQuestions: string[] = [];
                    if (content?.correct_questions && Array.isArray(content.correct_questions)) {
                      correctQuestions.push(...content.correct_questions);
                    } else if (content?.correct_question) {
                      correctQuestions.push(content.correct_question);
                    } else if (q?.correctAnswers?.[0]?.answer) {
                      correctQuestions.push(q.correctAnswers[0].answer);
                    }
                    // Check if student question matches ANY correct question
                    return correctQuestions.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                  }

                  return null; // No override
                })();

                const displayIsCorrect = overrideEssayCorrectness !== null 
                  ? overrideEssayCorrectness 
                  : (derivedTF ? derivedTF.isAllCorrect : ua.is_correct);
                const displayTotalSub = derivedTF ? derivedTF.totalSub : ua.total_sub_questions;
                const displayCorrectCount = derivedTF ? derivedTF.correctCount : ua.correct_count;
                const displayWrongCount = displayTotalSub !== undefined && displayCorrectCount !== undefined
                  ? Math.max(displayTotalSub - displayCorrectCount, 0)
                  : undefined;

                      return (
                        <AnswerDetailItem key={ua.id || idx} $isCorrect={displayIsCorrect}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '15px' }}>
                            {globalQuestionNumber}. {typeId === 12 && Array.isArray(content.statements) ? 'True / False' : renderQuestionText()}
                            <span style={{ fontStyle: 'italic', fontSize: 12, color: '#999', marginLeft: '6px' }}>
                              [{lookupKey || ua.question_id || ua.question?.id || 'N/A'}]
                            </span>
                    </div>
                    {/* Display passage and statements for type 12 */}
                    {typeId === 12 && content?.passage && (
                      <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px', fontStyle: 'italic' }}>
                        {content.passage}
                      </div>
                    )}
                    {typeId === 12 && Array.isArray(content.statements) && (
                      <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
                        {content.statements.map((stmt: any, i: number) => (
                          <div key={i}>{i + 1}) {stmt.text}</div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>
                      <strong>Câu trả lời:</strong> {(() => {
                        if (typeId === 12 && Array.isArray(content.statements)) {
                          const parts = parseTFAnswerParts(ua.answer, content.statements.length);
                          return (
                            <span>
                              {content.statements.map((_: any, i: number) => {
                                const correctVal = content.statements[i]?.is_true ? 'T' : 'F';
                                const isCorrect = parts[i] === correctVal;
                                const displayAns = parts[i] === 'T' ? 'True' : parts[i] === 'F' ? 'False' : parts[i];
                                return (
                                  <span key={i}>
                                    {i > 0 && '  -  '}
                                    <span style={{ color: isCorrect ? '#65a30d' : '#dc2626', fontWeight: 600 }}>
                                      {i + 1}.{displayAns}
                                    </span>
                                  </span>
                                );
                              })}
                            </span>
                          );
                        }
                        return formattedAnswer;
                      })()}
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
                      <strong>Đáp án đúng:</strong> {(() => {
                        if (typeId === 12 && Array.isArray(content.statements)) {
                          return (
                            <span>
                              {content.statements.map((stmt: any, i: number) => {
                                const correctVal = stmt.is_true ? 'True' : 'False';
                                return (
                                  <span key={i}>
                                    {i > 0 && '  -  '}
                                    {i + 1}.{correctVal}
                                  </span>
                                );
                              })}
                            </span>
                          );
                        }
                        return formattedCorrect;
                      })()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ 
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 700,
                        background: displayIsCorrect ? '#d1fae5' : '#fee2e2',
                        color: displayIsCorrect ? '#065f46' : '#991b1b'
                      }}>
                        {displayIsCorrect ? '✓ Đúng' : '✗ Sai'}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                        Điểm: {ua.score}
                      </span>
                    </div>
                    {displayTotalSub && displayTotalSub > 1 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: '13px' }}>
                        {displayCorrectCount !== undefined && displayCorrectCount > 0 && (
                          <span style={{ padding: '4px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 6 }}>
                            ✓ Đúng({displayCorrectCount})
                          </span>
                        )}
                        {displayWrongCount !== undefined && displayWrongCount > 0 && (
                          <span style={{ padding: '4px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
                            ✗ Sai({displayWrongCount})
                          </span>
                        )}
                      </div>
                      )}
                    </AnswerDetailItem>
                      );
                    })}
                  </div>
                );
              })}
            </AnswerDetailsList>

            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <button
                onClick={() => {
                  setResult(null);
                  window.location.reload();
                }}
                style={{
                  padding: '14px 32px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Làm lại bài thi
              </button>
            </div>
          </ResultCard>
        </Page>
      )}
      
      {showConfirmModal && (
        <ConfirmOverlay onClick={handleCancelSubmit}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>⚠️ Các câu chưa làm</ConfirmTitle>
            <ConfirmMessage>
              Bạn vẫn còn {undoneQuestions.length} câu chưa làm. Bạn có chắc chắn muốn nộp bài không?
            </ConfirmMessage>
            {undoneQuestions.length > 0 && (
              <UndoneCList>
                {undoneQuestions.map((q, idx) => (
                  <UndoneItem key={idx}>
                    Câu {q.number}: {q.text.substring(0, 60)}{q.text.length > 60 ? '...' : ''}
                  </UndoneItem>
                ))}
              </UndoneCList>
            )}
            <ConfirmActions>
              <ConfirmBtn $variant="ghost" onClick={handleCancelSubmit}>
                Tiếp tục làm bài
              </ConfirmBtn>
              <ConfirmBtn onClick={performSubmit} disabled={submitting}>
                {submitting ? 'Đang nộp...' : 'Nộp bài'}
              </ConfirmBtn>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
      
      {/* Only show exam content if no result is displayed (or if authenticated user) */}
      {(!result || authUser) && (
      <Page>
        <Card>
          {loading ? (
            <div>Đang tải đề thi...</div>
          ) : error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : !exam ? (
            <div>Không tìm thấy đề thi</div>
          ) : (
            <>
              <Header>
                {authUser && (
                  <BackButton onClick={() => navigate('/student')} title="Quay lại">
                    ← Quay lại
                  </BackButton>
                )}
                <TitleSection>
                  <Title>{exam.title}</Title>
                  <Meta>
                    <span>⏱️ {exam.duration} phút</span>
                    {exam.total_marks && <span>⭐ {exam.total_marks} điểm</span>}
                  </Meta>
                </TitleSection>
                {exam.duration ? (
                  <TimerBox title="Thời gian còn lại">
                    <span role="img" aria-label="timer">⏳</span>
                    <span>{remainingSec !== null ? fmt(remainingSec) : '--:--'}</span>
                  </TimerBox>
                ) : (
                  <TimerBox title="Thời gian làm bài">
                    <span role="img" aria-label="timer">⏱️</span>
                    <span>{fmt(elapsedSec)}</span>
                  </TimerBox>
                )}
              </Header>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                  Tên của bạn *
                  {authUser && <span style={{ fontSize: '12px', fontWeight: 400, color: '#666', marginLeft: '8px' }}>(Tự động từ tài khoản)</span>}
                </label>
                <Input
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  placeholder={authUser ? "" : "Nhập tên để bắt đầu"}
                  disabled={!!authUser}
                  style={authUser ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
              </div>

              <ContentGrid>
                <div>
                  <div style={{ marginBottom: 12, fontWeight: 700 }}>
                    Câu hỏi ({flatQuestions.length}):
                  </div>

                  {groupedByType.map((group, exerciseIdx) => {
                    const typeInfo = group.typeInfo || {};
                    const headerText = `Exercise ${exerciseIdx + 1}: ${typeInfo.eng_title || typeInfo.vn_title || `Type ${group.typeId}`}${typeInfo.vn_title && typeInfo.eng_title ? ` (${typeInfo.vn_title})` : ''}`;
                    return (
                      <div key={`exercise-${group.typeId}`}>
                        <ExerciseHeader>{headerText}</ExerciseHeader>
                        {group.questions.map((eq: any, idx: number) => {
                          const q = eq.question;
                          const qid = q.id;
                          const globalIndex = questionNumberMap[qid] || idx + 1;

                          return (
                            <QuestionBox key={qid} ref={(el) => { questionRefs.current[qid] = el; }}>
                              <QuestionTitle>
                                {globalIndex}. {q.question_type_id === 12 ? 'True / False' : renderQuestionText(q)}
                                {q.question_type_id !== 12 && (
                                  <span style={{fontStyle: 'italic', color: '#666', fontSize: '13px'}}>({qid})</span>
                                )}
                              </QuestionTitle>
                              {renderQuestionBody(q)}
                            </QuestionBox>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <RightCol>
                  <NavCard>
                    <NavHeader>
                      <span>Tiến độ</span>
                      <span style={{ fontSize: 12, color: '#475569' }}>{flatQuestions.length} câu</span>
                    </NavHeader>
                    <div style={{ marginBottom: 10 }}>
                      {exam.duration ? (
                        <TimerBox title="Thời gian còn lại">
                          <span role="img" aria-label="timer">⏳</span>
                          <span>{remainingSec !== null ? fmt(remainingSec) : '--:--'}</span>
                        </TimerBox>
                      ) : (
                        <TimerBox title="Thời gian làm bài">
                          <span role="img" aria-label="timer">⏱️</span>
                          <span>{fmt(elapsedSec)}</span>
                        </TimerBox>
                      )}
                    </div>
                    <TagGrid>
                      {questionTags.map((tag) => (
                        <Tag key={tag.id} done={isAnswered(tag.id)} onClick={() => scrollToQuestion(tag.id)}>
                          Câu {tag.label}
                        </Tag>
                      ))}
                    </TagGrid>
                  </NavCard>
                </RightCol>
                
                <div style={{ gridColumn: '1 / 2' }}>
                  <StickyActions>
                    {!participantName.trim() && (
                      <div style={{ color: 'red', marginBottom: 8, fontWeight: 600 }}>
                        Nhập tên để kích hoạt nút Nộp bài
                      </div>
                    )}
                    <Button onClick={handleSubmit} disabled={submitDisabled}>
                      {submitting ? 'Đang nộp...' : 'Nộp bài'}
                    </Button>
                  </StickyActions>

                  {result && (
                    <ResultBox>
                      <div><strong>Kết quả:</strong> {result.total_score} / {result.max_score}</div>
                      <div style={{ fontSize: 13, marginTop: 4, color: '#0f172a' }}>
                        Tự động chấm theo đáp án. Giáo viên có thể xem chi tiết bài làm.
                      </div>
                    </ResultBox>
                  )}
                </div>
              </ContentGrid>
            </>
          )}
        </Card>
      </Page>
      )}
    </>
  );
};

export default PublicExamPage;
